// Copyright ©️ 2026 eVolve MEP, LLC

import { useSyncExternalStore, useEffect } from 'react';

import type { RenderedTagsStore } from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/createRenderedTagsStore';
import {
  type TagInteractionCallbacks,
  TagLabel,
} from 'components/Autodesk/extensions/DimensioningExtension/ElementTags/TagLabel';

type Props = {
  renderedTagsStore: RenderedTagsStore;
  callbacks: TagInteractionCallbacks;
};

/**
 * It's currently possible to add *so many tags* that the page becomes unresponsive.
 *
 * For now, limit the number of tags rendered at once time so the page does not lock up.
 */
const TAG_RENDER_LIMIT = 500;

/**
 * Renders the list of `TagLabel` from the `renderedTagsStore`.
 * Updates are frequent, so keep this as low in the component tree as possible to avoid unnecessary re-renders of other components.
 */
export const TagLabelsOverlay = ({ renderedTagsStore, callbacks }: Props) => {
  const tags = useSyncExternalStore(renderedTagsStore.subscribe, renderedTagsStore.getSnapshot);
  useEffect(() => {
    if (tags.length > TAG_RENDER_LIMIT) {
      // oxlint-disable-next-line no-console
      console.warn(
        `Limiting tag rendering; tag count (${tags.length}) exceeds renderable maximum (${TAG_RENDER_LIMIT}).`,
      );
    }
  }, [tags.length]);

  let visibleTagCounter = 0;
  return tags.map((tag) => {
    if (tag.visible) {
      // oxlint-disable-next-line react/immutability
      visibleTagCounter += 1;
      if (visibleTagCounter < TAG_RENDER_LIMIT) {
        return <TagLabel key={tag.elementTagId} tag={tag} callbacks={callbacks} />;
      }
    }
    return null;
  });
};
